import type { Dispatch, SetStateAction, SyntheticEvent } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import clsx from 'clsx'
import { useCenter } from 'hooks/useCenter'
import _ from 'lodash'
import { SelectFormControl } from 'style'
import 'utils/mst.scss'

import type { MstCenter } from 'api-client'
import type { AutocompleteCenter, MstCenterSeq } from 'hooks/useCenter'

interface SelectCenterProps {
  trCompanyId: number | null | undefined
  centerId?: number | null | undefined
  orgCenterId?: string | null | undefined
  centerIds?: number[]  // Initial centerIds for multiple mode
  setCenterId?: Dispatch<SetStateAction<number | null | undefined>>
  setOrgCenterId?: Dispatch<SetStateAction<string | null | undefined>>
  setCenterInfo: Dispatch<SetStateAction<MstCenter | undefined>>
  isRequired: boolean
  setIsValid: Dispatch<SetStateAction<boolean>>
  enabled?: boolean
  isMultiple?: boolean
  setCenterIdList?: Dispatch<SetStateAction<number[]>>
  setOrgCenterIdList?: Dispatch<SetStateAction<string[]>>
  setCenterInfoList?: Dispatch<SetStateAction<MstCenterSeq[]>>
  startSelectTop?: boolean
  dropdownListControlId?: string
}

function SelectCenter({
  trCompanyId,
  centerId,
  orgCenterId,
  centerIds,
  setCenterId,
  setOrgCenterId,
  setCenterInfo,
  isRequired,
  setIsValid,
  enabled,
  isMultiple,
  setCenterIdList,
  setOrgCenterIdList,
  setCenterInfoList,
  startSelectTop,
  dropdownListControlId,
}: SelectCenterProps) {
  const { loginUserInfo } = useAuthContext()
  const uniqueId = useId()
  const dropdownListProps = dropdownListControlId
    ? ({ 'data-control-id': dropdownListControlId } as React.HTMLAttributes<HTMLUListElement>)
    : undefined

  const { centerSearch, autocompletCenters, fetchCenterSearch, isLoading } = useCenter()

  const [trCenter, setTrCenter] = useState<AutocompleteCenter[]>([])
  const [trValue, setTrValue] = useState<AutocompleteCenter | undefined | null>(undefined)
  const [trValueList, setTrValueList] = useState<AutocompleteCenter[]>([])
  const isFirstRenderRef = useRef(true)
  const hasInitializedMultipleRef = useRef(false)  // Track if multiple mode has been initialized
  const prevTrCompanyIdRef = useRef<number | null | undefined>(undefined)
  // Store initial centerId/orgCenterId to use when data loads, even if prop gets reset
  const initialCenterIdRef = useRef<number | null | undefined>(undefined)
  const initialOrgCenterIdRef = useRef<string | null | undefined>(undefined)
  const initialCenterIdsRef = useRef<number[] | undefined>(undefined)
  // Track previous single values to avoid unnecessary updates in multiple mode
  const prevSingleCenterIdRef = useRef<number | null | undefined>(undefined)
  const prevSingleOrgCenterIdRef = useRef<string | null | undefined>(undefined)

  // Store initial centerId/orgCenterId/centerIds when first set (not undefined/null)
  // This allows us to use the initial value even if the prop gets reset before data loads
  useEffect(() => {
    if (centerId != null && initialCenterIdRef.current === undefined) {
      initialCenterIdRef.current = centerId
    }
    if (orgCenterId != null && initialOrgCenterIdRef.current === undefined) {
      initialOrgCenterIdRef.current = orgCenterId
    }
    if (centerIds && centerIds.length > 0 && initialCenterIdsRef.current === undefined) {
      initialCenterIdsRef.current = centerIds
    }
  }, [centerId, orgCenterId, centerIds])

  // Create a Map for faster center lookup
  const centerMapById = useMemo(() => {
    const map = new Map<number, MstCenter>()
    centerSearch?.list.forEach((center) => {
      if (center.centerId != null) {
        map.set(center.centerId, center)
      }
    })
    return map
  }, [centerSearch?.list])

  const centerMapByOrgId = useMemo(() => {
    const map = new Map<string, MstCenter>()
    centerSearch?.list.forEach((center) => {
      if (center.orgCenterId != null) {
        map.set(center.orgCenterId, center)
      }
    })
    return map
  }, [centerSearch?.list])

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      // Only clear centerId/orgCenterId if trCompanyId actually changed (not just on re-render)
      const trCompanyIdChanged = prevTrCompanyIdRef.current !== trCompanyId

      if (trCompanyIdChanged) {
        if (setCenterId) {
          setTrValue(undefined)
          setCenterId(undefined)
        } else if (setOrgCenterId) {
          setTrValue(undefined)
          setOrgCenterId(undefined)
        }

        // Clear multiple selection values and reset initialization flag
        if (isMultiple) {
          setTrValueList([])
          setCenterIdList?.([])
          setOrgCenterIdList?.([])
          setCenterInfoList?.([])
          hasInitializedMultipleRef.current = false
        }
      }
    }

    prevTrCompanyIdRef.current = trCompanyId
    const companyIdToUse = trCompanyId === undefined || trCompanyId === null || trCompanyId === loginUserInfo?.companyId
      ? loginUserInfo?.companyId
      : trCompanyId

    if (companyIdToUse) {
      fetchCenterSearch({ enabled: enabled ?? undefined, companyId: companyIdToUse }, false)
    }
  }, [trCompanyId, loginUserInfo?.companyId, enabled, isMultiple, setCenterId, setOrgCenterId, setCenterIdList, setOrgCenterIdList, setCenterInfoList, fetchCenterSearch])

  useEffect(() => {
    // センターAPIが呼びs出されレスポンスが返ってきた場合のみ実行
    if (centerSearch.list.length) {
      setTrCenter(autocompletCenters)

      if (!isMultiple) {
        // Single selection mode
        if (setCenterId && centerId != null) {
          setTrValue(_.find(autocompletCenters, { centerId }))
        } else if (setOrgCenterId && orgCenterId != null) {
          setTrValue(_.find(autocompletCenters, { orgCenterId }))
        }
      }
      if (isMultiple) {
        // Multiple selection mode - initialize from centerIds prop or initialCenterIdsRef
        // Only initialize ONCE when we have centerIds to set and haven't initialized yet
        if (!hasInitializedMultipleRef.current) {
          const idsToUse = centerIds?.length ? centerIds : initialCenterIdsRef.current
          if (idsToUse && idsToUse.length > 0) {
            // Find matching centers from autocompletCenters
            const matchedCenters = autocompletCenters.filter(c =>
              c.centerId != null && idsToUse.includes(c.centerId)
            )
            if (matchedCenters.length > 0) {
              setTrValueList(matchedCenters)
              // Also update parent state
              const ids = matchedCenters.map(c => c.centerId).filter((id): id is number => id != null)
              setCenterIdList?.(ids)
              const infos = matchedCenters
                .map(c => c.centerId != null ? centerMapById.get(c.centerId) : undefined)
                .filter((info): info is MstCenterSeq => info !== undefined)
              setCenterInfoList?.(infos)
              hasInitializedMultipleRef.current = true
            }
          } else {
            // No centerIds to initialize, mark as initialized
            hasInitializedMultipleRef.current = true
          }
        }
      }

      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false
      }
    }
  }, [centerSearch, autocompletCenters, isMultiple, centerId, orgCenterId, centerIds, setCenterId, setOrgCenterId, setCenterIdList, setCenterInfoList, centerMapById])

  // When parent clears centerIds in multiple mode, also clear local selection list.
  useEffect(() => {
    if (isMultiple && (!centerIds || centerIds.length === 0)) {
      setTrValueList([])
    }
  }, [isMultiple, centerIds])

  // クリアボタン処理
  useEffect(() => {
    if (!isMultiple) {
      // Single selection mode
      if (setCenterId && !setOrgCenterId) {
        if (centerId === undefined && trValue) {
          setTrValue(undefined)
        }
      }
      else if (setOrgCenterId && !setCenterId) {
        if (orgCenterId === undefined && trValue) {
          setTrValue(undefined)
        }
      }
    }
    // Multiple selection mode doesn't need this clear logic as it's handled differently
  }, [centerId, orgCenterId, setCenterId, setOrgCenterId, trValue, isMultiple])

  // 必須チェック処理
  useEffect(() => {
    // 必須チェックを上位コンポーネントに通知
    const isValid = !isMultiple ? !!trValue : trValueList.length > 0
    setIsValid(!isValid)
  }, [trValue, trValueList, isMultiple, setIsValid])


  // Helper function to handle single selection
  const handleSingleSelection = useCallback((selectedValue: AutocompleteCenter | null) => {
    if (selectedValue) {
      setTrValue(selectedValue)

      if (setCenterId && selectedValue.centerId != null) {
        setCenterId(selectedValue.centerId)
        const centerInfo = centerMapById.get(selectedValue.centerId)
        if (centerInfo) {
          setCenterInfo(centerInfo)
        }
      }

      if (setOrgCenterId && selectedValue.orgCenterId != null) {
        setOrgCenterId(selectedValue.orgCenterId)
        const centerInfo = centerMapByOrgId.get(selectedValue.orgCenterId)
        if (centerInfo) {
          setCenterInfo(centerInfo)
        }
      }

      // Always set multiple values for consistency
      const singleCenterIds = selectedValue.centerId != null ? [selectedValue.centerId] : []
      const orgCenterIds = selectedValue.orgCenterId != null ? [selectedValue.orgCenterId] : []
      const centerInfo = selectedValue.centerId != null ? centerMapById.get(selectedValue.centerId) : undefined
      const centerInfos = centerInfo ? [centerInfo as MstCenterSeq] : []

      setCenterIdList?.(singleCenterIds)
      setOrgCenterIdList?.(orgCenterIds)
      setCenterInfoList?.(centerInfos)
    } else {
      setTrValue(null)
      if (setCenterId) setCenterId(undefined)
      if (setOrgCenterId) setOrgCenterId(undefined)
      setCenterInfo(undefined)
      setCenterIdList?.([])
      setOrgCenterIdList?.([])
      setCenterInfoList?.([])
    }
  }, [setCenterId, setOrgCenterId, setCenterInfo, setCenterIdList, setOrgCenterIdList, setCenterInfoList, centerMapById, centerMapByOrgId])

  // Helper function to handle multiple selection
  const handleMultipleSelection = useCallback((selectedValues: AutocompleteCenter[]) => {
    setTrValueList(selectedValues)

    const selectedCenterIds = selectedValues
      .map(v => v.centerId)
      .filter((id): id is number => id !== null && id !== undefined)
    const orgCenterIds = selectedValues
      .map(v => v.orgCenterId)
      .filter((id): id is string => id !== null && id !== undefined)
    const centerInfos = selectedValues
      .map(v => v.centerId != null ? centerMapById.get(v.centerId) : undefined)
      .filter((info): info is MstCenterSeq => info !== undefined)

    setCenterIdList?.(selectedCenterIds)
    setOrgCenterIdList?.(orgCenterIds)
    setCenterInfoList?.(centerInfos)

    // Also set single values for backward compatibility
    // Only update when value actually changes to prevent unnecessary re-renders that close the dropdown
    if (selectedValues.length > 0) {
      const firstValue = selectedValues[0]
      if (setCenterId && firstValue.centerId != null) {
        // Only update if value actually changed to prevent unnecessary re-renders
        if (prevSingleCenterIdRef.current !== firstValue.centerId) {
          prevSingleCenterIdRef.current = firstValue.centerId
          setCenterId(firstValue.centerId)
          const centerInfo = centerMapById.get(firstValue.centerId)
          if (centerInfo) {
            setCenterInfo(centerInfo)
          }
        }
      }
      if (setOrgCenterId && firstValue.orgCenterId != null) {
        // Only update if value actually changed to prevent unnecessary re-renders
        if (prevSingleOrgCenterIdRef.current !== firstValue.orgCenterId) {
          prevSingleOrgCenterIdRef.current = firstValue.orgCenterId
          setOrgCenterId(firstValue.orgCenterId)
          const centerInfo = centerMapByOrgId.get(firstValue.orgCenterId)
          if (centerInfo) {
            setCenterInfo(centerInfo)
          }
        }
      }
    } else {
      if (setCenterId && prevSingleCenterIdRef.current !== undefined) {
        prevSingleCenterIdRef.current = undefined
        setCenterId(undefined)
      }
      if (setOrgCenterId && prevSingleOrgCenterIdRef.current !== undefined) {
        prevSingleOrgCenterIdRef.current = undefined
        setOrgCenterId(undefined)
      }
      setCenterInfo(undefined)
    }
  }, [setCenterId, setOrgCenterId, setCenterInfo, setCenterIdList, setOrgCenterIdList, setCenterInfoList, centerMapById, centerMapByOrgId])

  const getOptionLabel = useCallback((option: AutocompleteCenter) => {
    const name = option.label ?? ''
    const id = option.orgCenterId ?? ''
    return id ? `[${id}]${name}` : name
  }, [])

  const handleAutocompleteChange = useCallback((e: SyntheticEvent<Element, Event>, selectedValue: any) => {
    handleSingleSelection(selectedValue as AutocompleteCenter | null)
  }, [handleSingleSelection])

  const handleOptionEqualToValue = useCallback((option: AutocompleteCenter, value: AutocompleteCenter) => {
    if (setCenterId) {
      return option.centerId === value.centerId
    }
    return option.orgCenterId === value.orgCenterId
  }, [setCenterId])

  const handleRenderInput = useCallback((params: any) => {
    const hasError = isRequired && !trValue
    return (
      <TextField
        label="センター"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        className={clsx('select-center-text-field')}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
          readOnly: true,
          tabIndex: -1,
        }}
        error={hasError}
        // helperText={hasError ? '必須' : ''}
        required={isRequired}
      />
    )
  }, [isLoading, isRequired, trValue])

  return (
    <SelectFormControl isMultiple={isMultiple}>
      {isMultiple ? (
        <Autocomplete
          multiple
          disableCloseOnSelect
          limitTags={1}
          id={`center-select-${uniqueId}`}
          sx={{ width: '100%' }}
          value={trValueList}
          options={trCenter}
          loading={isLoading}
          disabled={isLoading}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) => option.centerId === value.centerId}
          onChange={(_e, selectedValues) => {
            handleMultipleSelection(selectedValues)
          }}
          renderOption={(props, option, { selected }) => {
            // eslint-disable-next-line react/prop-types
            const { key, ...optionProps } = props
            const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon
            return (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li key={key} {...optionProps}>
                <SelectionIcon
                  fontSize="small"
                  style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
                />
                {getOptionLabel(option)}
              </li>
            )
          }}
          renderInput={(params) => {
            const hasError = isRequired && trValueList.length === 0
            return (
              <TextField
                label="センター"
                placeholder="センター"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                className={clsx('select-center-text-field')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                error={hasError}
                required={isRequired}
              />
            )
          }}
          slotProps={{
            popper: { placement: startSelectTop ? 'top-start' : 'bottom-start' },
          }}
          ListboxProps={
            dropdownListProps
          }
        />
      ) : (
        <Autocomplete
          key={`center-${trValue?.centerId ?? 'empty'}`}
          disablePortal
          id={`center-${uniqueId}`}
          value={trValue ?? null}
          options={trCenter}
          loading={isLoading}
          disabled={isLoading}
          freeSolo={false}
          selectOnFocus={false}
          clearOnBlur={false}
          handleHomeEndKeys
          getOptionLabel={getOptionLabel}
          onChange={handleAutocompleteChange}
          isOptionEqualToValue={handleOptionEqualToValue}
          renderInput={handleRenderInput}
          slotProps={{
            popper: { placement: startSelectTop ? 'top-start' : 'bottom-start' },
          }}
          ListboxProps={
            dropdownListProps
          }
        />
      )}
    </SelectFormControl>
  )
}

export default SelectCenter
